import React from "react";
import { Link, useNavigate } from "react-router-dom";
import { motion } from "framer-motion";
import { Button } from "../components/ui/button";
import { Badge } from "../components/ui/badge";
import { Sparkles, Zap, Layers, Users, Play, Check, ArrowRight, Film } from "lucide-react";
import { Logo } from "../components/Logo";
import { useAuth } from "../lib/auth";
import { useQuery } from "@tanstack/react-query";
import { api } from "../lib/api";
import { fadeUp, stagger, hoverLift, scaleIn } from "../lib/motions";

const HERO_BG = "https://images.unsplash.com/photo-1672872476232-da16b45c9001?crop=entropy&cs=srgb&fm=jpg&ixid=M3w4NjY2NzF8MHwxfHNlYXJjaHwxfHxmdXR1cmlzdGljJTIwZ2xvd2luZyUyMG5lb24lMjBiYXphYXIlMjBtYXJrZXR8ZW58MHx8fHwxNzgzODQzOTUyfDA&ixlib=rb-4.1.0&q=85";

const FEATURES = [
  { icon: Layers, title: "Multi-reference", desc: "Combine multiple reference images and videos in one generation." },
  { icon: Users, title: "100% character consistency", desc: "Lock characters across scenes with reference-driven models." },
  { icon: Zap, title: "State-of-the-art models", desc: "Seedance 2.0, Veo 3.1, Kling, Sora 2 — all under one roof." },
  { icon: Film, title: "Cinematic quality", desc: "Native audio, up to 1080p, up to 15s per clip." },
];

export default function Landing() {
  const navigate = useNavigate();
  const { user } = useAuth();
  const { data: modelsData } = useQuery({
    queryKey: ["models"],
    queryFn: async () => (await api.get("/models")).data,
  });
  const { data: plansData } = useQuery({
    queryKey: ["plans"],
    queryFn: async () => (await api.get("/plans")).data,
  });
  const models = modelsData?.models || [];
  const plans = plansData?.plans || [];

  return (
    <div className="relative min-h-screen bg-[#09090B] text-zinc-100 overflow-x-hidden">
      {/* Nav */}
      <header className="sticky top-0 z-40 glass border-b border-white/5">
        <div className="max-w-7xl mx-auto px-5 md:px-8 h-16 flex items-center justify-between">
          <Logo />
          <nav className="hidden md:flex items-center gap-8 text-sm text-zinc-400">
            <a href="#models" className="hover:text-white transition-colors">Models</a>
            <a href="#features" className="hover:text-white transition-colors">Features</a>
            <a href="#pricing" className="hover:text-white transition-colors">Pricing</a>
          </nav>
          <div className="flex items-center gap-2">
            {user ? (
              <Button data-testid="landing-open-studio" onClick={() => navigate("/studio")} className="btn-primary rounded-full px-5">
                Open Studio <ArrowRight className="ml-1.5 w-4 h-4" />
              </Button>
            ) : (
              <>
                <Link to="/login" data-testid="landing-signin" className="text-sm text-zinc-300 hover:text-white px-4 py-2">Sign in</Link>
                <Button data-testid="landing-signup" onClick={() => navigate("/signup")} className="btn-primary rounded-full px-5">
                  Start free
                </Button>
              </>
            )}
          </div>
        </div>
      </header>

      {/* Hero */}
      <section className="relative overflow-hidden grain">
        <div className="absolute inset-0 -z-10">
          <img src={HERO_BG} className="w-full h-full object-cover opacity-25" alt="" />
          <div className="absolute inset-0 bg-gradient-to-b from-[#09090B]/60 via-[#09090B]/85 to-[#09090B]" />
        </div>
        <div className="ambient-glow top-20 left-1/2 -translate-x-1/2 w-[600px] h-[600px] opacity-30" style={{ background: "#D946EF" }} />

        <div className="relative max-w-6xl mx-auto px-5 md:px-8 py-20 md:py-32">
          <motion.div initial="hidden" animate="show" variants={stagger(0.1, 0.12)}>
            <motion.div variants={fadeUp}>
              <Badge data-testid="hero-eyebrow" className="rounded-full bg-white/5 border border-white/10 text-cyan-400 uppercase tracking-[0.24em] text-[10px] px-4 py-1.5 mb-8">
                <Sparkles className="w-3 h-3 mr-2 inline" /> The AI video marketplace
              </Badge>
            </motion.div>

            <motion.h1 variants={fadeUp} className="font-display text-5xl md:text-7xl font-extrabold tracking-tighter leading-[1.03] max-w-4xl">
              Every video model.<br />
              <span className="bg-clip-text text-transparent" style={{ backgroundImage: "linear-gradient(90deg, #D946EF, #06B6D4)" }}>
                One bazaar.
              </span>
            </motion.h1>
            <motion.p variants={fadeUp} className="mt-6 text-lg md:text-xl text-zinc-400 max-w-2xl leading-relaxed">
              Seedance 2.0, Veo 3.1, Kling, Sora 2 — the world's best AI video models, unified with multi-reference support and 100% character consistency.
            </motion.p>

            <motion.div variants={fadeUp} className="mt-10 flex flex-wrap gap-3">
              <Button data-testid="hero-cta-primary" size="lg" onClick={() => navigate(user ? "/studio" : "/signup")} className="btn-primary rounded-full h-12 px-7 text-base font-semibold">
                {user ? "Open Studio" : "Start creating free"} <ArrowRight className="ml-2 w-4 h-4" />
              </Button>
              <Button data-testid="hero-cta-secondary" size="lg" variant="outline" onClick={() => document.getElementById("models")?.scrollIntoView({ behavior: "smooth" })}
                className="rounded-full h-12 px-7 text-base border-white/15 bg-white/5 hover:bg-white/10 text-white">
                <Play className="mr-2 w-4 h-4" /> Explore models
              </Button>
            </motion.div>

            <motion.div variants={stagger(0.4, 0.08)} className="mt-12 grid grid-cols-2 md:grid-cols-4 gap-4 max-w-3xl">
              {["Seedance 2.0", "Veo 3.1", "Kling", "Sora 2"].map((m) => (
                <motion.div key={m} variants={fadeUp} className="glass rounded-2xl px-4 py-3 text-center">
                  <div className="text-[10px] uppercase tracking-widest text-zinc-500">Powered by</div>
                  <div className="text-sm font-semibold text-white mt-1">{m}</div>
                </motion.div>
              ))}
            </motion.div>
          </motion.div>
        </div>
      </section>

      {/* Features */}
      <section id="features" className="relative max-w-7xl mx-auto px-5 md:px-8 py-20">
        <motion.div initial={{ opacity: 0, y: 20 }} whileInView={{ opacity: 1, y: 0 }} viewport={{ once: true, margin: "-100px" }} transition={{ duration: 0.5 }} className="max-w-2xl">
          <div className="text-xs uppercase tracking-[0.24em] text-cyan-400 mb-4">Why AI Bazaar</div>
          <h2 className="font-display text-3xl md:text-5xl font-extrabold tracking-tighter leading-tight">
            Built for creators<br />who ship real work.
          </h2>
        </motion.div>
        <motion.div initial="hidden" whileInView="show" viewport={{ once: true, margin: "-80px" }} variants={stagger(0.1, 0.08)}
                    className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-4 mt-12">
          {FEATURES.map((f, i) => (
            <motion.div key={i} variants={fadeUp} whileHover={{ y: -4 }} transition={{ duration: 0.2 }}
                        data-testid={`feature-${i}`} className="p-6 rounded-2xl bg-white/[0.02] border border-white/10 hover:border-white/20 transition-colors duration-200">
              <div className="w-11 h-11 rounded-xl flex items-center justify-center bg-white/5 border border-white/10 mb-4">
                <f.icon className="w-5 h-5 text-fuchsia-400" strokeWidth={2} />
              </div>
              <div className="font-display font-bold text-lg">{f.title}</div>
              <p className="text-sm text-zinc-400 mt-2 leading-relaxed">{f.desc}</p>
            </motion.div>
          ))}
        </motion.div>
      </section>

      {/* Models */}
      <section id="models" className="relative max-w-7xl mx-auto px-5 md:px-8 py-20">
        <motion.div initial={{ opacity: 0, y: 20 }} whileInView={{ opacity: 1, y: 0 }} viewport={{ once: true, margin: "-100px" }} transition={{ duration: 0.5 }}
                    className="flex items-end justify-between mb-10 flex-wrap gap-4">
          <div>
            <div className="text-xs uppercase tracking-[0.24em] text-cyan-400 mb-3">The catalog</div>
            <h2 className="font-display text-3xl md:text-5xl font-extrabold tracking-tighter">Meet the models</h2>
          </div>
          <p className="text-zinc-400 text-sm max-w-md">Curated flagship models. Each with different strengths — pick the one that matches your shot.</p>
        </motion.div>
        <motion.div initial="hidden" whileInView="show" viewport={{ once: true, margin: "-60px" }} variants={stagger(0.1, 0.05)}
                    className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-5">
          {models.map((m) => (
            <motion.div key={m.id} variants={fadeUp} whileHover={{ y: -4 }} transition={{ duration: 0.2 }}
                        data-testid={`model-card-${m.id}`} className="group relative rounded-2xl overflow-hidden bg-white/[0.02] border border-white/10 hover:border-white/25 transition-colors duration-200">
              <div className="aspect-[16/10] overflow-hidden">
                <img src={m.thumbnail} className="w-full h-full object-cover transition-transform duration-500 group-hover:scale-105" alt={m.name} />
                <div className="absolute inset-0 bg-gradient-to-t from-[#09090B] via-[#09090B]/30 to-transparent" />
              </div>
              <div className="p-5 -mt-16 relative">
                <div className="flex items-center gap-2 mb-2">
                  <span className="text-[10px] uppercase tracking-[0.2em] text-zinc-400">{m.vendor}</span>
                  {m.available ? (
                    <span className="text-[10px] px-2 py-0.5 rounded-full bg-emerald-500/15 text-emerald-400 border border-emerald-500/30">Available</span>
                  ) : (
                    <span className="text-[10px] px-2 py-0.5 rounded-full bg-amber-500/15 text-amber-400 border border-amber-500/30">Coming Soon</span>
                  )}
                </div>
                <div className="font-display font-extrabold text-xl">{m.name}</div>
                <p className="text-sm text-zinc-400 mt-2 leading-relaxed line-clamp-3">{m.description}</p>
                <div className="flex flex-wrap gap-1.5 mt-4">
                  {m.tags.slice(0, 3).map((t) => (
                    <span key={t} className="text-[10px] px-2 py-1 rounded-full bg-white/5 border border-white/10 text-zinc-400">{t}</span>
                  ))}
                </div>
              </div>
            </motion.div>
          ))}
        </motion.div>
      </section>

      {/* Pricing */}
      <section id="pricing" className="relative max-w-7xl mx-auto px-5 md:px-8 py-20">
        <motion.div initial={{ opacity: 0, y: 20 }} whileInView={{ opacity: 1, y: 0 }} viewport={{ once: true, margin: "-100px" }} transition={{ duration: 0.5 }} className="text-center mb-14">
          <div className="text-xs uppercase tracking-[0.24em] text-cyan-400 mb-3">Simple pricing</div>
          <h2 className="font-display text-3xl md:text-5xl font-extrabold tracking-tighter">Pick a plan, ship in minutes</h2>
        </motion.div>
        <motion.div initial="hidden" whileInView="show" viewport={{ once: true, margin: "-60px" }} variants={stagger(0.1, 0.08)}
                    className="grid grid-cols-1 md:grid-cols-3 gap-5 max-w-5xl mx-auto">
          {plans.map((p) => (
            <motion.div key={p.id} variants={fadeUp} whileHover={{ y: -4 }} transition={{ duration: 0.2 }}
                        data-testid={`plan-${p.id}`}
              className={`relative p-7 rounded-2xl bg-white/[0.02] border transition-colors duration-200 ${
                p.featured ? "border-fuchsia-500/50 md:scale-[1.02]" : "border-white/10 hover:border-white/20"
              }`}>
              {p.featured && (
                <div className="absolute -top-3 left-6 text-[10px] uppercase tracking-[0.24em] px-3 py-1 rounded-full bg-gradient-to-r from-fuchsia-500 to-cyan-500 text-white font-semibold">
                  Most popular
                </div>
              )}
              <div className="text-xs uppercase tracking-widest text-zinc-500">{p.tagline}</div>
              <div className="font-display font-extrabold text-2xl mt-1">{p.name}</div>
              <div className="mt-4 flex items-baseline gap-1">
                <span className="font-display text-5xl font-black tracking-tighter">${p.price_monthly}</span>
                <span className="text-zinc-500 text-sm">/mo</span>
              </div>
              <div className="text-xs text-zinc-500 mt-1">{p.credits_monthly} credits</div>
              <Button data-testid={`plan-cta-${p.id}`} onClick={() => navigate(user ? "/studio" : "/signup")}
                className={`w-full mt-6 rounded-full h-11 font-semibold ${p.featured ? "btn-primary" : "bg-white/8 border border-white/15 hover:bg-white/12 text-white"}`}>
                {p.cta}
              </Button>
              <ul className="mt-6 space-y-3">
                {p.features.map((f) => (
                  <li key={f} className="flex items-start gap-2 text-sm text-zinc-300">
                    <Check className="w-4 h-4 mt-0.5 text-cyan-400 shrink-0" strokeWidth={2.5} />
                    {f}
                  </li>
                ))}
              </ul>
            </motion.div>
          ))}
        </motion.div>
      </section>

      {/* CTA */}
      <section className="relative max-w-4xl mx-auto px-5 md:px-8 py-20">
        <motion.div initial={{ opacity: 0, scale: 0.95 }} whileInView={{ opacity: 1, scale: 1 }} viewport={{ once: true, margin: "-100px" }} transition={{ duration: 0.5 }}
                    className="p-10 md:p-14 rounded-3xl glass-strong text-center relative overflow-hidden">
          <div className="ambient-glow top-1/2 left-1/2 -translate-x-1/2 -translate-y-1/2 w-[500px] h-[300px] opacity-30" style={{ background: "#D946EF" }} />
          <h3 className="font-display text-3xl md:text-5xl font-extrabold tracking-tighter">Your next scene is one prompt away.</h3>
          <p className="mt-4 text-zinc-400">Free to start. No card required.</p>
          <Button data-testid="footer-cta" size="lg" onClick={() => navigate(user ? "/studio" : "/signup")} className="btn-primary rounded-full h-12 px-8 mt-8 text-base font-semibold">
            {user ? "Open Studio" : "Start free"} <ArrowRight className="ml-2 w-4 h-4" />
          </Button>
        </motion.div>
      </section>

      <footer className="border-t border-white/5 py-10 px-5 text-center text-xs text-zinc-500">
        © 2026 AI Bazaar. Made for creators.
      </footer>
    </div>
  );
}
